// BHP-1062: the shared VPS detail. The three product pages (generic, WordPress,
// Linked Helper) differ only in where "back" goes and what extra block they add,
// so the body lives here — same reasoning as VpsServerList for the overviews.
//
// Data comes from two places on purpose: `useVps` is our own row (hostname,
// labels), `useVpsServerState` is fetched from Hetzner per request because
// status and IPs are stale the moment they are stored (BHP-1067/1101).
//
// The price comes from the backend (BHP-1103), out of the same catalog row and
// customer margin the invoice uses. The old page multiplied Hetzner's list
// price by a hardcoded 5, which bore no relation to what anyone was billed.
// It is the recurring monthly amount, not usage accrued so far: billing runs
// on the 1st over the previous month and bills a full-month server at the
// monthly rate. Null means the backend had no honest figure — show that, never
// 0.00.
import { type ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { toUserMessage } from '../../../shared/http/errorMessage';
import {
  DEFAULT_PERIOD,
  formatMonthlyPriceNet,
  METRIC_PERIODS,
  type MetricPeriod,
  type VpsServerState
} from '../domain/vpsServerState';
import type { VpsSummary } from '../domain/vpsSummary';
import { editableLabels, type LabelRow } from '../domain/vpsLabels';
import { useVps, useVpsServerState } from '../application/vpsDetailQueries';
import {
  POWER_ACTIONS,
  type PowerAction,
  useVpsPowerAction
} from '../application/vpsPowerMutations';
import VpsEditModal from './VpsEditModal';
import VpsDeleteModal from './VpsDeleteModal';
import VpsMetricsPanel from './VpsMetricsPanel';
import InlineConfirm from '../../../shared/ui/InlineConfirm';

/**
 * BHP-1123: which actions ask first, and what they say. An action in here needs
 * confirming; one that is absent runs straight away — so this map is the list,
 * and copy can never drift from the set of actions that use it.
 *
 * Start is absent on purpose: it takes nothing away. Stop is here because the
 * server stays off until someone starts it again, which is at least as
 * disruptive as the restart that was the only action asking.
 */
const CONFIRMATIONS: Partial<
  Record<
    PowerAction,
    { question: (hostname: string) => ReactNode; cta: string }
  >
> = {
  stop: {
    question: (hostname) => (
      <>
        Stopping <strong>{hostname}</strong> takes it offline — it stays off
        until someone starts it again. Continue?
      </>
    ),
    cta: 'Yes, stop'
  },
  restart: {
    question: (hostname) => (
      <>
        Restarting interrupts everything running on <strong>{hostname}</strong>.
        Continue?
      </>
    ),
    cta: 'Yes, restart'
  }
};

const ACTION_LABELS: Record<PowerAction, string> = {
  start: 'Start',
  stop: 'Stop',
  restart: 'Restart'
};

/**
 * Which actions make sense for a status. Unknown status (Hetzner unreachable,
 * or a state we have not seen) leaves everything enabled rather than locking
 * the user out of the only buttons that could fix it.
 */
function isAllowed(action: PowerAction, status: string | null): boolean {
  if (status === null) {
    return true;
  }
  return action === 'start' ? status !== 'running' : status === 'running';
}

export default function VpsServerDetail({
  uuid,
  overviewPath,
  overviewLabel,
  extra
}: {
  uuid: string;
  overviewPath: string;
  overviewLabel: string;
  /**
   * Variant-specific block, rendered under the server card. Gets the live state
   * too, because Linked Helper builds a connection file from the IPv4.
   */
  extra?: (vps: VpsSummary, state: VpsServerState | undefined) => ReactNode;
}): JSX.Element {
  const vps = useVps(uuid);
  const state = useVpsServerState(uuid);
  const power = useVpsPowerAction(uuid);

  const [period, setPeriod] = useState<MetricPeriod>(DEFAULT_PERIOD);
  const [confirming, setConfirming] = useState<PowerAction | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  if (vps.isPending) {
    return <p className="text-sm text-slate-500">Loading server…</p>;
  }

  if (vps.isError || vps.data === undefined) {
    return (
      <div className="space-y-3">
        <p
          role="alert"
          className="rounded-lg border border-solid border-rose-200 bg-rose-50 p-3 text-sm text-rose-700"
        >
          {toUserMessage(vps.error, 'This server could not be loaded.')}
        </p>
        <BackLink to={overviewPath} label={overviewLabel} />
      </div>
    );
  }

  const server = vps.data;
  const status = state.data?.status ?? null;

  const run = (action: PowerAction): void => {
    setConfirming(null);
    // The hook owns the lifecycle now (BHP-1120): it stays busy while it polls
    // the Hetzner action to a final status, and surfaces its own error.
    power.run(action);
  };

  const onAction = (action: PowerAction): void => {
    if (CONFIRMATIONS[action] === undefined) {
      run(action);
      return;
    }
    setConfirming(action);
  };

  return (
    <section className="space-y-4">
      <BackLink to={overviewPath} label={overviewLabel} />

      <div className="rounded-xl border border-solid border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              {server.hostname}
            </h2>
            <StatusBadge status={status} isPending={state.isPending} />
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                setIsEditing(true);
              }}
              className="rounded-md border border-solid border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Edit
            </button>
            {POWER_ACTIONS.map((action) => (
              <button
                key={action}
                type="button"
                disabled={power.isBusy || !isAllowed(action, status)}
                onClick={() => {
                  onAction(action);
                }}
                className="rounded-md border border-solid border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                {power.pendingAction === action
                  ? `${ACTION_LABELS[action]}ing…`
                  : ACTION_LABELS[action]}
              </button>
            ))}
            {/* Set apart from the power actions: this one does not come back. */}
            <button
              type="button"
              onClick={() => {
                setIsDeleting(true);
              }}
              className="rounded-md border border-solid border-rose-300 bg-white px-3 py-1.5 text-sm font-medium text-rose-700 hover:bg-rose-50"
            >
              Delete
            </button>
          </div>
        </div>

        {confirming === null ? null : (
          <PowerConfirmation
            action={confirming}
            hostname={server.hostname}
            onConfirm={() => {
              run(confirming);
            }}
            onCancel={() => {
              setConfirming(null);
            }}
          />
        )}

        {power.error !== null ? (
          <p
            role="alert"
            className="mt-3 rounded border border-solid border-rose-200 bg-rose-50 p-2 text-sm text-rose-700"
          >
            {power.error}
          </p>
        ) : null}

        {state.isError ? (
          <p
            role="alert"
            className="mt-3 rounded border border-solid border-amber-200 bg-amber-50 p-2 text-sm text-amber-900"
          >
            {toUserMessage(
              state.error,
              'The live state of this server could not be loaded.'
            )}
          </p>
        ) : (
          <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
            <Fact label="IPv4" value={state.data?.publicIpv4} />
            <Fact label="IPv6" value={state.data?.publicIpv6} />
            <Fact label="Type" value={state.data?.serverType} />
            {/* Hetzner dropped `datacenter` from the server payload; the
                backend falls back to the location, so "fsn1" not "fsn1-dc8". */}
            <Fact label="Location" value={state.data?.datacenter} />
            <Fact
              label="vCPU"
              value={state.data?.cores?.toString() ?? undefined}
            />
            <Fact
              label="RAM"
              value={
                state.data?.memoryGb === null ||
                state.data?.memoryGb === undefined
                  ? undefined
                  : `${state.data.memoryGb} GB`
              }
            />
            <Fact
              label="Disk"
              value={
                state.data?.diskGb === null || state.data?.diskGb === undefined
                  ? undefined
                  : `${state.data.diskGb} GB`
              }
            />
            <Fact label="Created" value={state.data?.createdAt} />
            <Fact
              label="Price per month"
              value={
                // "Unavailable" rather than the em-dash the other facts use:
                // on a money field an em-dash reads as free (BHP-1103).
                formatMonthlyPriceNet(state.data) ?? 'Unavailable'
              }
              hint="excl. VAT"
            />
            <Fact
              label="Hetzner ID"
              value={server.hetznerId?.toString() ?? undefined}
            />
            <Labels rows={editableLabels(server.labels)} />
          </dl>
        )}
      </div>

      {isEditing ? (
        <VpsEditModal
          server={server}
          onClose={() => {
            setIsEditing(false);
          }}
        />
      ) : null}

      {isDeleting ? (
        <VpsDeleteModal
          server={server}
          overviewPath={overviewPath}
          onClose={() => {
            setIsDeleting(false);
          }}
        />
      ) : null}

      {extra?.(server, state.data)}

      <VpsMetricsPanel uuid={uuid} period={period} onPeriodChange={setPeriod} />
    </section>
  );
}

/**
 * The confirmation step in front of a power action, worded for the action it is
 * asking about. Renders nothing for an action that needs no confirming, so the
 * question and the "does this ask?" decision cannot disagree.
 */
function PowerConfirmation({
  action,
  hostname,
  onConfirm,
  onCancel
}: {
  action: PowerAction;
  hostname: string;
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element | null {
  const copy = CONFIRMATIONS[action];

  if (copy === undefined) {
    return null;
  }

  return (
    <div className="mt-3 rounded-lg border border-solid border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
      <InlineConfirm
        tone="warning"
        prompt={<p className="m-0">{copy.question(hostname)}</p>}
        confirmLabel={copy.cta}
        cancelLabel="Cancel"
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    </div>
  );
}

function BackLink({ to, label }: { to: string; label: string }): JSX.Element {
  return (
    <Link
      to={to}
      className="text-sm font-medium text-teal-700 no-underline hover:underline"
    >
      ← {label}
    </Link>
  );
}

function Fact({
  label,
  value,
  hint
}: {
  label: string;
  value?: string | null;
  hint?: string;
}): JSX.Element {
  return (
    <div>
      <dt className="text-slate-400">
        {label}
        {hint === undefined ? null : (
          <span className="ml-1 text-xs text-slate-400">({hint})</span>
        )}
      </dt>
      <dd className="font-medium text-slate-700">
        {value === null || value === undefined || value === '' ? '—' : value}
      </dd>
    </div>
  );
}

/**
 * BHP-1115: the customer's own labels, as one more fact.
 *
 * Only theirs: the labels this platform sets (customerUuid, server-type,
 * installation, ManagedBy, Client) are bookkeeping they never wrote and cannot
 * change, and `customerUuid` is an internal identifier. Showing them put a raw
 * UUID on the page and read like something the customer was supposed to act on.
 */
function Labels({ rows }: { rows: LabelRow[] }): JSX.Element {
  return (
    <div className="sm:col-span-2 lg:col-span-3">
      <dt className="text-slate-400">Labels</dt>
      <dd className="mt-1 flex flex-wrap gap-1">
        {rows.length === 0 ? (
          <span className="font-medium text-slate-700">—</span>
        ) : (
          rows.map((row) => (
            <span
              key={row.key}
              className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600"
            >
              {row.key}: {row.value}
            </span>
          ))
        )}
      </dd>
    </div>
  );
}

const STATUS_STYLES: Record<string, string> = {
  running: 'bg-teal-50 text-teal-700',
  off: 'bg-slate-100 text-slate-600',
  starting: 'bg-amber-50 text-amber-700',
  stopping: 'bg-amber-50 text-amber-700'
};

function StatusBadge({
  status,
  isPending
}: {
  status: string | null;
  isPending: boolean;
}): JSX.Element {
  if (isPending) {
    return <span className="text-xs text-slate-400">Checking status…</span>;
  }

  if (status === null) {
    return <span className="text-xs text-slate-400">Status unknown</span>;
  }

  const style = STATUS_STYLES[status] ?? 'bg-slate-100 text-slate-600';

  return (
    <span
      className={`mt-1 inline-block rounded px-2 py-0.5 text-xs font-medium ${style}`}
    >
      {status}
    </span>
  );
}

export { METRIC_PERIODS };
